import Decimal from "decimal.js-light";

import {
  getDrawingsFromSalesOrder,
  getProductsFromSalesOrderForCustomPdf,
} from "@/apiv2/customDeliveryDocket";
import { createAction, createSetterActions } from "@/redux/helpers/action";
import { handleError } from "@/redux/utils/error";
import {
  PRICING_STRATEGY_LINEAL_FEET_LABEL,
  PRICING_STRATEGY_LINEAL_METRES_LABEL,
  PRICING_STRATEGY_SQUARE_FEET_LABEL,
  PRICING_STRATEGY_SQUARE_METRES_LABEL,
  PRODUCT_TYPE_FLASHING_LABEL,
  PRODUCT_TYPE_NOTES_ITEM_LABEL,
  STATUS,
} from "@/utils/constants";

import { MODULE } from "./constants";
import * as selectors from "./products-section.selectors";

// Simple setter actions (dispatch with value)
export const [
  setErrors,
  setIsFetchingStatus,
  setDrawingsList,
  setDrawings,
  setProducts,
  setSelectedProductUids,
  setSelectedDrawingUids,
] = createSetterActions(MODULE, [
  "errors",
  "isFetchingStatus",
  "drawingsList",
  "drawingItems",
  "products",
  "selectedProductUids",
  "selectedDrawingUids",
]);

// Custom actions
export const reset = createAction(`${MODULE}/RESET`);

// --- Complex thunks ---

export const toggleProductKit =
  ({ kitUid, checked }) =>
  (dispatch, getState) => {
    const state = getState();
    const selectedProductUids = selectors.getSelectedProductUids(state);
    const { productKits } = selectors.groupedProducts(state);
    const newSelected = new Set(selectedProductUids);
    const kit = productKits.find((k) => k.uid === kitUid);

    if (!kit) {
      return;
    }

    if (checked) {
      newSelected.add(kitUid);
      kit.kitItems.forEach((item) => {
        newSelected.add(item.uid);
      });
      kit.subKits.forEach((subKit) => {
        newSelected.add(subKit.uid);
        subKit.items.forEach((subItem) => {
          newSelected.add(subItem.uid);
        });
      });
    } else {
      newSelected.delete(kitUid);
      kit.kitItems.forEach((item) => {
        newSelected.delete(item.uid);
      });
      kit.subKits.forEach((subKit) => {
        newSelected.delete(subKit.uid);
        subKit.items.forEach((subItem) => {
          newSelected.delete(subItem.uid);
        });
      });
    }

    dispatch(setSelectedProductUids(Array.from(newSelected)));
  };

export const toggleProductKitItem =
  ({ kitItemUid, parentKitUid, checked }) =>
  (dispatch, getState) => {
    const state = getState();
    const selectedProductUids = selectors.getSelectedProductUids(state);
    const { productKits } = selectors.groupedProducts(state);
    const newSelected = new Set(selectedProductUids);
    const kit = productKits.find((k) => k.uid === parentKitUid);

    if (!kit) {
      return;
    }

    if (checked) {
      newSelected.add(kitItemUid);
    } else {
      newSelected.delete(kitItemUid);
    }

    const allKitItems = kit.kitItems.map((item) => item.uid);
    const allSubKitIds = kit.subKits.map((subKit) => subKit.uid);
    const allChildren = [...allKitItems, ...allSubKitIds];
    const selectedChildren = allChildren.filter((id) => newSelected.has(id));

    if (selectedChildren.length > 0 && allChildren.length > 0) {
      newSelected.add(parentKitUid);
    } else {
      newSelected.delete(parentKitUid);
    }

    dispatch(setSelectedProductUids(Array.from(newSelected)));
  };

export const toggleSubKit =
  ({ subKitUid, parentKitUid, checked }) =>
  (dispatch, getState) => {
    const state = getState();
    const selectedProductUids = selectors.getSelectedProductUids(state);
    const { productKits } = selectors.groupedProducts(state);
    const newSelected = new Set(selectedProductUids);
    const kit = productKits.find((k) => k.uid === parentKitUid);
    const subKit = kit?.subKits.find((sk) => sk.uid === subKitUid);

    if (!kit || !subKit) {
      return;
    }

    if (checked) {
      newSelected.add(subKitUid);
      subKit.items.forEach((item) => {
        newSelected.add(item.uid);
      });
    } else {
      newSelected.delete(subKitUid);
      subKit.items.forEach((item) => {
        newSelected.delete(item.uid);
      });
    }

    const allKitItems = kit.kitItems.map((item) => item.uid);
    const allSubKitIds = kit.subKits.map((subKit) => subKit.uid);
    const allChildren = [...allKitItems, ...allSubKitIds];
    const selectedChildren = allChildren.filter((id) => newSelected.has(id));

    if (selectedChildren.length > 0 && allChildren.length > 0) {
      newSelected.add(parentKitUid);
    } else {
      newSelected.delete(parentKitUid);
    }

    dispatch(setSelectedProductUids(Array.from(newSelected)));
  };

export const toggleSubKitItem =
  ({ subKitItemUid, subKitUid, parentKitUid, checked }) =>
  (dispatch, getState) => {
    const state = getState();
    const selectedProductUids = selectors.getSelectedProductUids(state);
    const { productKits } = selectors.groupedProducts(state);
    const newSelected = new Set(selectedProductUids);
    const kit = productKits.find((k) => k.uid === parentKitUid);
    const subKit = kit?.subKits.find((sk) => sk.uid === subKitUid);

    if (!kit || !subKit) {
      return;
    }

    if (checked) {
      newSelected.add(subKitItemUid);
    } else {
      newSelected.delete(subKitItemUid);
    }

    const allSubKitItems = subKit.items.map((item) => item.uid);
    const selectedSubKitItems = allSubKitItems.filter((id) => newSelected.has(id));

    if (selectedSubKitItems.length > 0 && allSubKitItems.length > 0) {
      newSelected.add(subKitUid);
    } else {
      newSelected.delete(subKitUid);
    }

    const allKitItems = kit.kitItems.map((item) => item.uid);
    const allSubKitIds = kit.subKits.map((subKit) => subKit.uid);
    const allChildren = [...allKitItems, ...allSubKitIds];
    const selectedChildren = allChildren.filter((id) => newSelected.has(id));

    if (selectedChildren.length > 0 && allChildren.length > 0) {
      newSelected.add(parentKitUid);
    } else {
      newSelected.delete(parentKitUid);
    }

    dispatch(setSelectedProductUids(Array.from(newSelected)));
  };

export const toggleStandaloneProduct =
  ({ productUid, checked }) =>
  (dispatch, getState) => {
    const state = getState();
    const selectedProductUids = selectors.getSelectedProductUids(state);
    const newSelected = new Set(selectedProductUids);

    if (checked) {
      newSelected.add(productUid);
    } else {
      newSelected.delete(productUid);
    }

    dispatch(setSelectedProductUids(Array.from(newSelected)));
  };

export const setSelectedDrawingUidsAction =
  ({ uid, checked } = {}) =>
  (dispatch, getState) => {
    const state = getState();
    const selectedDrawingUids = selectors.getSelectedDrawingUids(state);

    dispatch(
      setSelectedDrawingUids(
        checked
          ? [...selectedDrawingUids, uid]
          : selectedDrawingUids.filter((selected) => selected !== uid)
      )
    );
  };

export const selectAllProductsAndDrawings = (status) => (dispatch, getState) => {
  const state = getState();

  if (status) {
    const drawings = selectors.drawingsFromSalesOrder(state).map((drawing) => drawing.item.uid);
    const products = selectors.productsFromSalesOrder(state).map((product) => product.uid);

    dispatch(setSelectedProductUids(products));
    dispatch(setSelectedDrawingUids(drawings));
  } else {
    dispatch(setSelectedProductUids([]));
    dispatch(setSelectedDrawingUids([]));
  }
};

export const fetchProductsAndDrawings =
  ({ customerUid, orderUid }) =>
  async (dispatch) => {
    try {
      dispatch(setIsFetchingStatus(STATUS.PENDING));

      const [{ data: products }, { data: drawings }] = await Promise.all([
        getProductsFromSalesOrderForCustomPdf({
          customerUid,
          orderUid,
          searchFilter: { include_notes: true },
        })(),
        getDrawingsFromSalesOrder({ customerUid, orderUid })(),
      ]);

      dispatch(
        setProducts(
          products
            .filter((p) => p.productType !== PRODUCT_TYPE_FLASHING_LABEL)
            .map((p) => {
              const remainingQuantity = parseFloat(p.deliveryDocketTracker?.remainingQuantity ?? 0);
              return {
                ...p,
                customQuantity: remainingQuantity,
              };
            })
        )
      );
      dispatch(setDrawings(drawings));
      dispatch(setIsFetchingStatus(STATUS.FULFILLED));
    } catch (error) {
      handleError(error, dispatch);
      dispatch(setIsFetchingStatus(STATUS.REJECTED));
    }
  };

export const updateProductQuantity =
  ({ uid, quantity }) =>
  (dispatch, getState) => {
    const state = getState();
    const products = selectors.productsFromSalesOrder(state);

    dispatch(
      setProducts(
        products.map((p) => {
          if (p.uid === uid) {
            return { ...p, customQuantity: quantity };
          }
          return p;
        })
      )
    );
  };

export const updateProductKitQuantity =
  ({ uid, quantity }) =>
  (dispatch, getState) => {
    const state = getState();
    const products = selectors.productsFromSalesOrder(state);

    let updatedProducts = products.map((p) => {
      if (p.uid === uid) {
        return { ...p, customQuantity: quantity };
      }
      if (
        p.parentItem === uid &&
        p.isKitItem &&
        ![
          PRICING_STRATEGY_LINEAL_METRES_LABEL,
          PRICING_STRATEGY_SQUARE_METRES_LABEL,
          PRICING_STRATEGY_LINEAL_FEET_LABEL,
          PRICING_STRATEGY_SQUARE_FEET_LABEL,
        ].includes(p.pricingStrategy)
      ) {
        return {
          ...p,
          customQuantity: Decimal(p.baseQuantity).times(quantity).toDecimalPlaces(4).toFixed(4),
        };
      }
      return p;
    });

    updatedProducts = updatedProducts.map((p) => {
      if (p.parentItem === uid && p.isSubKitParent) {
        return {
          ...p,
          customQuantity:
            parseFloat(quantity) > parseFloat(p.quantity) ? p.customQuantity : quantity,
        };
      }

      if (
        p.parentItem === uid &&
        p.isSubKitItem &&
        ![
          PRICING_STRATEGY_LINEAL_METRES_LABEL,
          PRICING_STRATEGY_SQUARE_METRES_LABEL,
          PRICING_STRATEGY_LINEAL_FEET_LABEL,
          PRICING_STRATEGY_SQUARE_FEET_LABEL,
        ].includes(p.pricingStrategy)
      ) {
        return {
          ...p,
          customQuantity: Decimal(p.baseQuantity).times(quantity).toDecimalPlaces(4).toFixed(4),
        };
      }
      return p;
    });
    dispatch(setProducts(updatedProducts));

    dispatch(resetQuanityToTheOriginalQuantity());
  };

export const updateProductKitItemQuantity =
  ({ uid, quantity }) =>
  (dispatch, getState) => {
    const state = getState();
    const products = selectors.productsFromSalesOrder(state);

    const updatedProducts = products.map((p) => {
      if (p.uid === uid && p.isKitItem) {
        const parentKit = products.find((product) => product.uid === p.parentItem);
        return {
          ...p,
          customQuantity: quantity,
          baseQuantity: Decimal(quantity)
            .dividedBy(parentKit.customQuantity)
            .toDecimalPlaces(4)
            .toFixed(4),
        };
      }
      return p;
    });

    dispatch(setProducts(updatedProducts));
  };

export const updateProductSubKitQuantity =
  ({ uid, quantity }) =>
  (dispatch, getState) => {
    const state = getState();
    const products = selectors.productsFromSalesOrder(state);

    const updatedProducts = products.map((p) => {
      if (p.uid === uid && p.isSubKitParent) {
        return { ...p, customQuantity: quantity };
      }

      if (
        p.parentSubKit === uid &&
        ![
          PRICING_STRATEGY_LINEAL_METRES_LABEL,
          PRICING_STRATEGY_SQUARE_METRES_LABEL,
          PRICING_STRATEGY_LINEAL_FEET_LABEL,
          PRICING_STRATEGY_SQUARE_FEET_LABEL,
        ].includes(p.pricingStrategy)
      ) {
        return {
          ...p,
          customQuantity: Decimal(p.baseQuantity).times(quantity).toDecimalPlaces(4).toFixed(4),
        };
      }
      return p;
    });

    dispatch(setProducts(updatedProducts));

    dispatch(resetQuanityToTheOriginalQuantity());
  };

export const updateProductSubKitItemQuantity =
  ({ uid, quantity }) =>
  (dispatch, getState) => {
    const state = getState();
    const products = selectors.productsFromSalesOrder(state);

    const updatedProducts = products.map((p) => {
      if (p.uid === uid && p.isSubKitItem) {
        const parentSubKit = products.find((product) => product.uid === p.parentSubKit);
        return {
          ...p,
          customQuantity: quantity,
          baseQuantity: Decimal(quantity)
            .dividedBy(parentSubKit.customQuantity)
            .toDecimalPlaces(4)
            .toFixed(4),
        };
      }
      return p;
    });

    dispatch(setProducts(updatedProducts));
  };

export const resetQuanityToTheOriginalQuantity = () => (dispatch, getState) => {
  const state = getState();
  const products = selectors.productsFromSalesOrder(state);

  dispatch(
    setProducts(
      products.map((p) => {
        if (
          [PRODUCT_TYPE_NOTES_ITEM_LABEL].includes(p.productType) ||
          [
            PRICING_STRATEGY_LINEAL_METRES_LABEL,
            PRICING_STRATEGY_SQUARE_METRES_LABEL,
            PRICING_STRATEGY_LINEAL_FEET_LABEL,
            PRICING_STRATEGY_SQUARE_FEET_LABEL,
          ].includes(p.pricingStrategy)
        ) {
          return p;
        }

        const remainingQuantity = parseFloat(p.deliveryDocketTracker?.remainingQuantity ?? 0);
        if (parseFloat(p.customQuantity) > remainingQuantity) {
          return { ...p, customQuantity: remainingQuantity };
        }
        return p;
      })
    )
  );
};
